import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    WorkOutline as WorkOutlineIcon,
    Place as PlaceIcon,
    EditLocationAlt as EditLocationAltIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { initializeDB } from '../../../config/db';
import { JHA_STORE, ALT_JOB_STORE, STATUS_NEW, STATUS_PROCESS } from '../../../config/constants';
import SelectEmployeeModal from '../SelectEmployeeModal';
import JhaStepHeader from '../JhaStepHeader';
import EvaluationSection from '../step2/EvaluationSection';
import { getFullNameEmployeeBy } from '../../../services/employeeService';
import { MODE_JOB, MODE_LOCATION, MODE_MANUAL, isLocationMode, ensureAllStepsStructure } from '../utils/stepTwoUtils';
import { createJhaSkeleton } from '../utils/jhaRecordFactory';
import { generateUUID } from '../../../utils/uuid';

const TODAY_STRING = format(new Date(), 'yyyy-MM-dd');

function buildLocationOptions(jobs, altJobs = []) {
    const seen = new Map();

    // Regular jobs first
    jobs.forEach(job => {
        const rawLocation = job.location || job.Location || '';
        if (!rawLocation) return;
        const key = rawLocation.trim().toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, {
                label: rawLocation,
                jobCodes: [job.Job],
                siteAddress: job.siteAddress || job.SiteAddress || '',
                isAltJob: false
            });
        } else {
            const entry = seen.get(key);
            if (job.Job && !entry.jobCodes.includes(job.Job)) {
                entry.jobCodes.push(job.Job);
            }
        }
    });

    // Alt jobs — only add new locations, no duplicates
    altJobs.forEach(altJob => {
        const rawLocation = altJob.Location || altJob.location || '';
        if (!rawLocation) return;
        const key = rawLocation.trim().toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, {
                label: rawLocation,
                jobCodes: [altJob.Job],
                siteAddress: '',
                isAltJob: true
            });
        } else {
            const entry = seen.get(key);
            if (altJob.Job && !entry.jobCodes.includes(altJob.Job)) {
                entry.jobCodes.push(altJob.Job);
            }
        }
    });

    return Array.from(seen.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
    );
}

function toDisplayName(employee) {
    if (!employee) return '';
    return typeof employee === 'string' ? employee : (employee.FullName || employee.fullName || '');
}

export default function JhaStepOne() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { isNew = false, jha = null, viewMode = false, fromViewer = false } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [altJobs, setAltJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [jhaData, setJhaData] = useState(null);
    const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);
    const [error, setError] = useState('');
    const evaluationSectionContainerRef = useRef(null);
    const evaluationSectionRef = useRef(null);
    const [invalidEvaluationItems, setInvalidEvaluationItems] = useState([]);

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const createdRecordIdRef = useRef(jha?.id || null);

    const isSubmitted = jhaData && (jhaData.status === 'finish' || jhaData.status === 'completed' || jhaData.syncStatus === 'synced');
    const isReadOnly = isSubmitted || viewMode;

    const locationOptions = useMemo(() => buildLocationOptions(jobs, altJobs), [jobs, altJobs]);

    // Sort jobs by Job number in descending order (highest to lowest)
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA; // Descending order
        });
    }, [jobs]);

    useEffect(() => {
        loadData();
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();

        const [allJobs, allEmployees, allDocs, allEvaluation, allAltJobs] = await Promise.all([
            db.transaction('job', 'readonly').objectStore('job').getAll(),
            db.transaction('employees', 'readonly').objectStore('employees').getAll(),
            db.transaction('docsPermit', 'readonly').objectStore('docsPermit').getAll(),
            db.transaction('evaluationOfWork', 'readonly').objectStore('evaluationOfWork').getAll(),
            db.objectStoreNames.contains(ALT_JOB_STORE)
                ? db.transaction(ALT_JOB_STORE, 'readonly').objectStore(ALT_JOB_STORE).getAll()
                : Promise.resolve([])
        ]);

        setJobs(allJobs || []);
        setAltJobs(allAltJobs || []);
        setEmployees((allEmployees || []).map(emp => ({
            ...emp,
            id: emp.id || emp.ID || emp.EmployeeId || `${emp.FullName}-${emp.Division}`
        })));

        const employeeId = localStorage.getItem('employeeId');
        const defaultPerson = employeeId ? await getFullNameEmployeeBy(employeeId) : '';

        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        let record = null;

        if (createdRecordIdRef.current) {
            record = await store.get(createdRecordIdRef.current);
        }

        if (!record && jha?.id) {
            record = await store.get(jha.id);
        }

        if (!record && isNew) {
            const newId = generateUUID();
            record = createJhaSkeleton({
                id: newId,
                status: STATUS_NEW,
                createdAt: new Date().toISOString(),
                defaultPerson,
                docs: allDocs,
                evaluation: allEvaluation,
                today: TODAY_STRING,
                metaLastStep: 1,
            });
            await store.put(record);
            createdRecordIdRef.current = record.id;
        }

        if (!record && jha) {
            const skeleton = createJhaSkeleton({
                id: jha.id,
                status: jha.status || STATUS_NEW,
                createdAt: jha.createdAt || new Date().toISOString(),
                defaultPerson,
                docs: allDocs,
                evaluation: allEvaluation,
                today: TODAY_STRING,
                metaLastStep: 1,
            });

            record = {
                ...skeleton,
                ...jha,
                stepOne: { ...skeleton.stepOne, ...(jha.stepOne || {}) },
                stepTwo: { ...skeleton.stepTwo, ...(jha.stepTwo || {}) },
                stepThree: { ...skeleton.stepThree, ...(jha.stepThree || {}) },
                stepFour: { ...skeleton.stepFour, ...(jha.stepFour || {}) },
                meta: { ...skeleton.meta, ...(jha.meta || {}) },
            };
            await store.put(record);
            createdRecordIdRef.current = record.id;
        }

        if (!record) {
            setLoading(false);
            navigate('/jha');
            return;
        }

        const structured = ensureAllStepsStructure(
            { ...record },
            allDocs,
            allEvaluation,
            defaultPerson,
            TODAY_STRING
        );

        if (structured.meta?.lastStep === undefined || structured.meta.lastStep < 1) {
            structured.meta = { ...(structured.meta || {}), lastStep: 1 };
            await store.put(structured);
        }

        setJhaData(structured);
        setLoading(false);
    }, [isNew, jha, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) {
            setSaving(false);
            return;
        }

        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(recordToSave);
        await tx.done;
        setSaving(false);
        window.dispatchEvent(new Event('jhaUpdated'));
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        const { immediate = false, minStep = 1 } = options;
        const currentLast = jhaData?.meta?.lastStep || 1;
        const recordLast = record?.meta?.lastStep || currentLast;
        const nextLast = Math.max(currentLast, recordLast, minStep);

        const recordWithMeta = {
            ...record,
            meta: { ...(record.meta || {}), lastStep: nextLast }
        };

        if (isReadOnly) {
            setJhaData(recordWithMeta);
            return;
        }

        setJhaData(recordWithMeta);
        pendingRecordRef.current = recordWithMeta;
        setSaving(true);

        if (immediate) {
            flushSave();
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            flushSave();
        }, 400);
    }, [flushSave, isReadOnly, jhaData]);

    const handleModeChange = (_, newMode) => {
        if (!newMode || !jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };
        updated.stepOne.mode = newMode;
        if (isLocationMode(newMode)) {
            updated.stepOne.jobNumber = '';
            updated.stepOne.projectName = '';
            updated.stepOne.jobSiteName = '';
            updated.stepOne.jobSiteAddress = '';
            updated.stepOne.taskLocation = '';
            // Между «из справочника» и «руками» подобранная локация не переносится:
            // выбранное значение и вбитый текст — разные вещи.
            updated.stepOne.locationName = '';
        } else {
            updated.stepOne.locationName = '';
            updated.stepOne.taskLocation = '';
        }
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        setError('');
        scheduleSave(updated, { minStep: 1 });
    };

    const handleJobSelect = (_, selectedJob) => {
        if (!jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };

        if (selectedJob) {
            updated.stepOne.jobNumber = selectedJob.Job || '';
            updated.stepOne.projectName = selectedJob.JobName || '';
            updated.stepOne.jobSiteName = selectedJob.location || selectedJob.Location || '';
            updated.stepOne.jobSiteAddress = selectedJob.siteAddress || selectedJob.SiteAddress || '';
            updated.stepOne.taskLocation = selectedJob.siteAddress || selectedJob.SiteAddress || '';
            updated.stepOne.locationName = '';
        } else {
            updated.stepOne.jobNumber = '';
            updated.stepOne.projectName = '';
            updated.stepOne.jobSiteName = '';
            updated.stepOne.jobSiteAddress = '';
            updated.stepOne.taskLocation = '';
        }

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        setError('');
        scheduleSave(updated, { minStep: 1 });
    };

    const handleLocationSelect = (_, selectedLocation) => {
        if (!jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };

        if (selectedLocation) {
            updated.stepOne.locationName = selectedLocation.label;
            updated.stepOne.taskLocation = selectedLocation.label;
        } else {
            updated.stepOne.locationName = '';
            updated.stepOne.taskLocation = '';
        }

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        setError('');
        scheduleSave(updated, { minStep: 1 });
    };

    const handleManualLocationChange = event => {
        if (!jhaData || isReadOnly) return;
        const value = event.target.value;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };
        updated.stepOne.locationName = value;
        updated.stepOne.taskLocation = value;

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        setError('');
        scheduleSave(updated, { minStep: 1 });
    };

    const handleDateChange = event => {
        if (!jhaData || isReadOnly) return;
        const value = event.target.value;
        if (!value) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne, date: value } };
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        setError('');
        scheduleSave(updated, { minStep: 1 });
    };

    const handleSupervisorSelection = employee => {
        if (!jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };
        updated.stepOne.jobSiteSupervisor = employee ? toDisplayName(employee) : '';
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { immediate: true, minStep: 1 });
    };

    const handleEvaluationSave = (value, selection) => {
        if (!jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepOne: { ...jhaData.stepOne } };
        updated.stepOne.evaluationOfWork = (updated.stepOne.evaluationOfWork || []).map(item =>
            item.value === value ? { ...item, selected: selection } : item
        );
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { minStep: 1 });

        if (selection === 'YES' || selection === 'N/A') {
            setInvalidEvaluationItems(prev => prev.filter(item => item !== value));
        } else {
            setInvalidEvaluationItems(prev => prev.includes(value) ? prev : [...prev, value]);
        }
    };

    const isDateValid = () => {
        if (!jhaData?.stepOne?.date) return false;
        return jhaData.stepOne.date >= TODAY_STRING;
    };

    const isEvaluationComplete = () => {
        if (!jhaData?.stepOne?.evaluationOfWork) return false;
        return jhaData.stepOne.evaluationOfWork.every(item => item.selected === 'YES' || item.selected === 'N/A');
    };

    const isSupervisorSelected = () => Boolean(toDisplayName(jhaData?.stepOne?.jobSiteSupervisor));

    const isGeneralInfoComplete = () => {
        if (!jhaData) return false;
        const { stepOne } = jhaData;
        if (!stepOne) return false;
        if (!isDateValid() || !isSupervisorSelected()) return false;

        if (stepOne.mode === MODE_JOB) {
            return Boolean(stepOne.jobNumber);
        }
        if (isLocationMode(stepOne.mode)) {
            return Boolean((stepOne.locationName || '').trim());
        }
        return false;
    };

    const handleNext = async () => {
        const missingEvaluation = (jhaData?.stepOne?.evaluationOfWork || [])
            .filter(item => item.selected !== 'YES' && item.selected !== 'N/A')
            .map(item => item.value);

        if (!isGeneralInfoComplete() || missingEvaluation.length) {
            setInvalidEvaluationItems(missingEvaluation);
            setError('Complete general information and evaluation before continuing.');

            if (missingEvaluation.length) {
                const firstMissing = missingEvaluation[0];

                // Scroll to first invalid item in evaluation section
                setTimeout(() => {
                    if (firstMissing && evaluationSectionRef.current?.scrollToValue) {
                        evaluationSectionRef.current.scrollToValue(firstMissing, { behavior: 'smooth', offset: 10, contextRows: 3 });
                    }
                }, 100);
            }
            return;
        }

        setInvalidEvaluationItems([]);
        setError('');
        const updated = {
            ...jhaData,
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 2)
            }
        };
        setJhaData(updated);
        pendingRecordRef.current = updated;
        await flushSave();
        navigate('/jha/step-2', { state: { isNew: false, jha: updated, viewMode } });
    };

    if (loading || !jhaData) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const { stepOne } = jhaData;
    const selectedJob = jobs.find(job => job.Job === stepOne.jobNumber) || null;
    const selectedLocation = stepOne.mode === MODE_LOCATION && stepOne.locationName
        ? locationOptions.find(option => option.label === stepOne.locationName) || null
        : null;

    return (
        <Box sx={{
            height: 'calc(100vh - 64px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <JhaStepHeader
                currentStep={1}
                jhaData={jhaData}
                fromViewer={fromViewer}
            />

            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', minHeight: 0 }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1100,
                        display: 'flex',
                        flexDirection: 'column',
                        py: 1
                    }}
                >
                    <Card elevation={1} sx={{ mt: 1, mb: { xs: 1.25, md: 1.75 } }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Stack spacing={1.25}>
                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Work Context
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={stepOne.mode}
                                    exclusive
                                    onChange={handleModeChange}
                                    disabled={isReadOnly}
                                    size="small"
                                >
                                    <ToggleButton value={MODE_JOB} sx={{ px: 2 }}>
                                        <WorkOutlineIcon sx={{ mr: 1 }} fontSize="small" /> Job
                                    </ToggleButton>
                                    <ToggleButton value={MODE_LOCATION} sx={{ px: 2 }}>
                                        <PlaceIcon sx={{ mr: 1 }} fontSize="small" /> Location
                                    </ToggleButton>
                                    <ToggleButton value={MODE_MANUAL} sx={{ px: 2 }}>
                                        <EditLocationAltIcon sx={{ mr: 1 }} fontSize="small" /> Manual
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            <Grid
                                container
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, md: 1.5 }}
                                alignItems="center"
                            >
                                <Grid item xs={12} sm={5} md={5} sx={{ pl: { xs: '0 !important', sm: '0 !important', md: '0 !important' } }}>
                                    {stepOne.mode === MODE_MANUAL ? (
                                        <TextField
                                            value={stepOne.locationName || ''}
                                            onChange={handleManualLocationChange}
                                            label="Location"
                                            placeholder="Enter location"
                                            size="small"
                                            fullWidth
                                            disabled={isReadOnly}
                                        />
                                    ) : stepOne.mode === MODE_JOB ? (
                                        <Autocomplete
                                            value={selectedJob}
                                            onChange={handleJobSelect}
                                            options={sortedJobs}
                                            getOptionLabel={option => {
                                                const jobCode = option.Job || '';
                                                const jobName = option.JobName || '';
                                                return jobCode && jobName ? `${jobCode} • ${jobName}` : (jobCode || jobName || 'Unnamed job');
                                            }}
                                            renderInput={params => (
                                                <TextField {...params} label="Job" placeholder="Search job" size="small" />
                                            )}
                                            disabled={isReadOnly}
                                            clearOnBlur={false}
                                        />
                                    ) : (
                                        <Autocomplete
                                            value={selectedLocation}
                                            onChange={handleLocationSelect}
                                            options={locationOptions}
                                            getOptionLabel={option => option.label}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.label}>
                                                    {option.label}
                                                    {option.isAltJob && (
                                                        <span style={{
                                                            marginLeft: 8,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: '#e3f2fd',
                                                            color: '#1565c0',
                                                            padding: '1px 6px',
                                                            borderRadius: 4,
                                                            fontWeight: 600
                                                        }}>ALT</span>
                                                    )}
                                                </li>
                                            )}
                                            renderInput={params => (
                                                <TextField {...params} label="Location" placeholder="Search location" size="small" />
                                            )}
                                            disabled={isReadOnly}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={3} md={3}>
                                    <TextField
                                        type="date"
                                        size="small"
                                        fullWidth
                                        label="Date"
                                        value={stepOne.date || TODAY_STRING}
                                        onChange={handleDateChange}
                                        disabled={isReadOnly}
                                        inputProps={{ min: TODAY_STRING }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} md={4}>
                                    <TextField
                                        value={toDisplayName(stepOne.jobSiteSupervisor)}
                                        size="small"
                                        fullWidth
                                        label="Supervisor"
                                        placeholder="Select supervisor"
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <PersonIcon fontSize="small" sx={{ color: '#607d8b' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        onClick={!isReadOnly ? () => setSupervisorModalOpen(true) : undefined}
                                        onKeyDown={!isReadOnly ? (event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSupervisorModalOpen(true);
                                            }
                                        } : undefined}
                                        sx={{
                                            cursor: isReadOnly ? 'default' : 'pointer',
                                            '& .MuiOutlinedInput-input': {
                                                cursor: isReadOnly ? 'default' : 'pointer'
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </CardContent>
                </Card>

                    <Card elevation={1} sx={{ mt: 1, mb: 1 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Evaluation of Work Area
                            </Typography>
                            <Box ref={evaluationSectionContainerRef}>
                                <EvaluationSection
                                    ref={evaluationSectionRef}
                                    jhaData={jhaData}
                                    isReadOnly={isReadOnly}
                                    availableOptions={['YES', 'N/A']}
                                    invalidValues={invalidEvaluationItems}
                                    onSaveEvaluation={handleEvaluationSave}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #e0e0e0',
                    zIndex: 5
                }}
            >
                <Stack spacing={1} sx={{ px: 0.75, py: 0.6 }}>
                    {error && (
                        <Alert severity="warning" onClose={() => setError('')} sx={{ m: 0 }}>
                            {error}
                        </Alert>
                    )}
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        {saving && (
                            <Typography variant="caption" color="text.secondary">
                                Saving…
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                        >
                            Continue to Activities
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <SelectEmployeeModal
                open={supervisorModalOpen}
                onClose={() => setSupervisorModalOpen(false)}
                employees={employees}
                onSelect={handleSupervisorSelection}
                title="Select Supervisor"
                defaultSelectedName={toDisplayName(stepOne.jobSiteSupervisor)}
            />
        </Box>
    );
}
